
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

function WritingChapterDetail() {
    const { chapterId } = useParams();
    const navigate = useNavigate();

    // Full chapters content
    const chapters = [
        {
            id: 1,
            title: 'Introduction',
            content: `INTRODUCTION
In the CELPIP Writing module, you'll have a total of 53 minutes to complete two distinct tasks. For CELPIP Writing Task 1, you'll have 27 minutes to compose an email on a specified topic. In CELPIP Writing Task 2, you'll have 26 minutes to craft an opinion-based response to a survey question.

WRITING TASK 1 : WRITING AN EMAIL
WRITING TASK 2 : RESPONDING TO A SURVEY QUESTIONS

CELPIP -Marks Evaluation Criteria :- 

Coherence/Meaning
Lexical Range (Vocabulary)
Readability / Comprehensibility
Task Fulfillment

COHERENCE/MEANING: Convey a message that is concise, well-structured, and straightforward, making it effortless for the reader to comprehend and follow.
VOCABULARY : Employ a diverse and apt vocabulary to articulate your thoughts precisely, while also seamlessly assembling advanced words and phrases to convey your message in a natural and fluid manner.
READABILITY/COMPREHENSIBILITY : Utilize a language structure that enhances the clarity and comprehensibility of your writing. Arrange your content into thoughtfully constructed paragraphs for a well-organized presentation.
TASK FULFILLMENT : Ensure that you adequately and proficiently cover all aspects of the prompt, leaving no elements unaddressed.
The evaluation process involves combining the four category ratings provided by a single rater into a single score for each task. Each task is assessed by two different raters, and the resulting four task scores are then amalgamated to calculate an overall score for your Writing skills. It's important to note that each individual task contributes equally, with each task accounting for 50% of your final Writing score.`
        },
        {
            id: 2,
            title: 'Writing Task 1: Email Strategies',
            content: `[ CELPIP WRITING TASK - 1 ]
Writing - Email Strategies

The first task is to write an email regarding day-to-day matters. The topics are relatable as they are mostly things which happen in everyday life. The time allotted for completion of this task is 27 minutes and expected word count is 150-200 words.

Useful Features :
The CELPIP-General Writing Test incorporates several distinctive features not commonly found in other language proficiency exams:

1. Spell Check : Unlike many other language tests, the Writing Test includes a spell check feature. This feature aligns with everyday writing practices, minimizing the risk of spelling errors. However, it is crucial to exercise caution when using this tool. While it can flag spelling mistakes, it won't assist in selecting the correct word from a list of possible options. This necessitates a strong command of English vocabulary to make the right choice. When a spelling error is detected, the computer will underline the word with a thin red line, similar to what you see in most word processing programs. To access a list of potential word replacements, right-click on the word. If you spot the correct word, click on it to replace the misspelled one.

2. Editing Functions : The test allows users to utilize basic editing functions such as cut, paste, copy, delete, and undo/redo. These functions can be accessed easily by right-clicking with the mouse. If you are comfortable with these tools, you can make use of them during the test for swift and efficient editing. However, if you prefer not to use these functions, you are not obligated to do so. Nevertheless, it's important to recognize that these editing tools enable quick and convenient editing of your written content.
3. Word Count : Each writing task specifies a word count range, and it's essential to stay within 10% of that range to avoid potential penalties. Both tasks have a word count range of 150–200 words. Therefore, if your response falls below 135 words or exceeds 220 words, it may lead to a lower score. You will find the word count displayed immediately below the typing area for both parts of the Writing Test.

These unique features enhance the convenience and accuracy of the CELPIP-General Writing Test, allowing test-takers to produce well-crafted written responses effectively.

IMPORTANT POINTS & STRATEGIES WHILE WRITING :-
STEPS TO FOLLOW :
Comprehend the Prompt :  Begin by thoroughly understanding the entire prompt, ensuring that you grasp its essence.
Determine the Objective : Identify the primary purpose of the task. Are you expected to express dissatisfaction, praise someone, or narrate an incident?
Select a Suitable Scenario : Choose a situation that aligns with the task's requirements and allows you to include all necessary details.
Analyze the Bullet Points : Examine each bullet point and brainstorm various ideas or details that you can incorporate for each point.
Comprehensive Coverage : Ensure that you address each bullet point in the prompt to meet the task's criteria.
Structure Your Email : Consider whether you need introductory and concluding paragraphs and, if so, determine their content.
Begin Writing : Start composing your email, following your plan if it's effective, but be open to adjustments if needed.
Original Expression : Avoid echoing the words and phrases from the prompt. Strive for original ways to convey key ideas.
Rich Vocabulary : Utilize descriptive and expressive vocabulary to precisely convey your thoughts.
Sentence Variety : Diversify your sentence structure, incorporating a mix of simple, compound, and complex sentences to maintain reader engagement.
Transitions and Connectors : Employ appropriate transitional words and phrases to establish clear connections between sentences and paragraphs.
Email Format : Keep in mind that you are crafting an email message and format your response accordingly.
Review for Logic and Organization : If your planning was sound, your email should be logically structured. Reevaluate it with a fresh perspective and make improvements if necessary.
Word Count Check : Ensure that your email adheres to the specified word count. Make adjustments by either trimming or adding content if needed.
Grammar and Language : Scrutinize your email for grammar, punctuation, syntax, sentence structure, paragraphing, formatting, word choice, and word count. Make improvements wherever possible.
Final Check : If time allows, perform one last review of your response, making any last-minute enhancements to enhance its quality.

ALSO REMEMBER :
Appropriate Tone: Tailor your tone to match the context, whether formal or informal, based on the recipient and situation.
Stay Focused: Stick to the task at hand and avoid excessive personal introductions or unrelated information.
Emotional Control: Express disappointment or dissatisfaction when necessary but refrain from extreme anger or threats.
Effective Phrasing: Word choice and sentence structure significantly impact your message's effectiveness. Choose your words carefully.
Provide Adequate Information: Ensure that your email contains sufficient information to support your request or message.
IMPORTANT :
Make the most of your writing time by revising your email message. Even if you complete the initial draft ahead of time, use the remaining time to refine your tone, content, phrasing, and word choice. Effective writing takes time and effort, and enhancing the quality of your writing will ultimately result in a higher score.`
        },
        {
            id: 3,
            title: 'Templates for Writing Task 1',
            content: `Templates for Writing Task 1:

1. Formal Email:
Written to someone you do not know and are writing to for the first time. Examples: complaint, request, suggestion (personal, business, recent experience, product etc.)
Structure:
- Start: Dear Sir or Madam (or Dear Mr./Ms. Last Name)
- Body: Answer all 3 questions in bullets, 1 sentence + 2 supporting sentences each.
- Closing: Yours sincerely, Full Name

2. Informal Email:
Written to a friend, colleague, or a person you know. Example: invitation (party, dinner, celebration)
Structure:
- Start: Dear First Name
- Body: Answer all 3 questions in bullets, 1 sentence + 2 supporting sentences each.
- Closing: Yours truly, First Name

3. Semi-Formal Email:
Written to someone we know but are not well-acquainted with and maintain a formal relation. Example: feedback to party organizer, teacher, landlord.
Structure:
- Same as Formal Email but tone is slightly more casual.`
        },
        {
            id: 4,
            title: 'Sample Emails',
            content: `Chapter 4: Sample Formal and Informal Emails

SAMPLE FORMAL EMAIL
Practice Test 1: You are volunteering at a children's charity. Write an email to a local company (150-200 words).

Sample Email:
Dear Sir,

I am writing this letter to introduce the charity in Toronto represented by me called "Belief". The mission of this organization is to support and foster underprivileged children by providing books, materials, and nutritional food as needed.

We are hosting an event on the 25th of January at the district centre, displaying artefacts made by the children and highlighting their achievements. This event will help raise funds for them as well as enable an audacious opportunity for them to obtain scholarships from different corporate entities. We would appreciate your attendance along with your colleagues and any sponsorship you can offer. The money collected during the event would be used for the betterment of the children and advancement of their future plans.

Yours sincerely,
Sarah William

SAMPLE INFORMAL EMAIL
Practice Test 2: An overseas friend wants to visit your country. Write an email including:
- Recommend the best time of year to visit and why
- Describe the types of accommodation
- Say what you would like to show your friend

Sample Email:
Dear Sam,

I hope this email finds you in great spirits. I'm thrilled to hear that you're considering a visit to Canada. Firstly, the best time to visit Canada would be during Summer. During this time, the weather is at its most pleasant, with mild temperatures and less rainfall, making it ideal for outdoor activities and sightseeing. You'll have the chance to enjoy our beautiful landscapes, festivals, and cultural events.

When it comes to accommodation, Canada offers a wide range of options to suit your preferences and budget. You can choose from luxurious hotels in the heart of the city, charming boutique guesthouses in picturesque towns, or even cozy cabins nestled in the countryside. Depending on your itinerary, I can help you find the perfect place to stay that will enhance your overall experience.

During your visit, I'd love to show you some of the must-see attractions and hidden gems in Canada. From exploring historic landmarks and vibrant markets to embarking on breathtaking hikes in our national parks, there's something here for every traveler. We can also savor our delicious local cuisine and immerse ourselves in the rich culture and traditions of Canada.

Let me know your travel dates, and I'll start planning an itinerary that will make your visit truly memorable.

Yours truly,
John`
        },
        {
            id: 5,
            title: 'Advanced Vocabulary for writing',
            content: `Advanced vocabulary for writing:
Ecstatic - feeling or expressing overwhelming happiness or delight
She was ecstatic when she received the news of her promotion.
Dismayed - feeling concerned, disappointed, or discouraged.
I was dismayed by the sudden drop in my grades.
Mournful - expressing or feeling sorrow or grief.
The mournful music at the funeral brought tears to everyone's eyes.
Content - feeling satisfied with one's situation or life.
Sitting by the fireplace, he felt content with a good book in his hand.
Anxious - experiencing uneasiness or worry about future events.
She was anxious about her upcoming job interview.
Exhilarated - filled with excitement, energy, or enthusiasm.
The roller coaster ride left him feeling exhilarated and thrilled.
Frustrated - feeling annoyed or discouraged when goals or desires are not
met.
After hours of searching, he grew frustrated with the lost keys.
Serene - calm and peaceful.
The view of the tranquil lake at sunset made her feel serene.
Confused - having a lack of understanding or clarity.
The complex instructions left her feeling confused about the task.
Delighted - feeling great pleasure and satisfaction.
She was delighted to receive a handwritten letter from her friend.
Resigned - accepting something undesirable because it can't be changed.
After missing the bus, he felt resigned to the fact that he would be late
for work.
Nervous - experiencing anxiety or fear about an upcoming event.
She felt nervous before her first public speaking engagement.
Wistful - having a feeling of longing or nostalgia for something in the past.
Looking at old photographs made him feel wistful about his childhood.
Indifferent - having no particular interest or concern; neutral.
He seemed indifferent to the outcome of the game; he didn't care who
won.
Jubilant - feeling or expressing great joy, especially in a public and
triumphant way.
The jubilant crowd celebrated their team's victory with cheers and
applause.
Irritated - feeling annoyed or agitated.
His constant interruptions during the meeting left everyone irritated.
Contemplative - thinking deeply or reflectively.
Sitting by the window, she appeared contemplative, lost in thought.
Pleased - feeling a sense of satisfaction or happiness.
The positive feedback from her boss left her feeling very pleased.
Aghast - feeling shocked or horrified.
She was aghast when she saw the extent of the damage after the storm.

Embarrassed - feeling self-conscious, typically due to a mistake or a situation.
He turned red and felt embarrassed when he realized his zipper was
open.
Optimistic - having a positive outlook on future events or outcomes.
Despite the challenges, she remained optimistic about her business's
success.
Fascinated - feeling a strong interest or curiosity about something.
She was fascinated by the intricate details of the antique watch.
Miffed - feeling slighted or annoyed over a minor issue.
She was miffed that her friend forgot her birthday.
Thrilled - feeling intense excitement or happiness.
He was thrilled when he found out he won the lottery.
Cautious - feeling careful and wary to avoid potential harm or danger.
She was cautious about giving her personal information online.
Disgusted - feeling a strong revulsion or aversion to something unpleasant.
The sight of the spoiled food in the fridge left her disgusted.
Eager - feeling enthusiastic and eager to do something.
The children were eager to open their presents on Christmas morning.
Gloomy - feeling sad or pessimistic.
The rainy, overcast day made her feel gloomy.
Humbled - feeling modest or having a sense of humility.
Winning the award left her feeling humbled and grateful.
Impatient - feeling a lack of patience; wanting something to happen quickly.
He grew impatient waiting in the long line at the store.
`
        },
        {
            id: 6,
            title: 'Checklist for Writing',
            content: `Important things to remember
Set word count(150- 200).
No contractions(don’t, can’t).
No exclamation marks as the formal style requires a neutral tone.
There is a space between paragraphs which is required by the exam readability criterion.
Used a variety of grammatical structures. 
Perfect Tenses(I have noticed).
Passive Voice(the issue was reported).
Modal Verbs(the measures must be taken).
There are at least 10 academic words or phrases in my essay to highlight:
cause and effect(therefore, thus)
sequence(finally, furthermore)
comparison(likewise, nevertheless)
contrast(despite, although)
examples(for instance)
conclusion(to summarize, in other words)
Their purpose is to make a transition to the next paragraph coherent.
No lexical items are repeated, and synonyms are used instead.
Words like very, big, and small which are considered fairly basic are replaced by their more sophisticated equivalents — extremely, large, tiny, and the like.
Proofreading.
Avoid using phrasal verbs (verb + preposition combination — give up, run into) in the formal context since their casualty contradicts the overall business - like letter format.

Formal and Informal Vocabulary: -

Formal vocabulary and structures
Informal vocabulary and structures
Request
Purchase
Could
Would
At your earliest convenience
Response write back
Cannot
Your sincerely
Furthermore
Can i suggest
As we described above
Ask
Buy
Can
Will
When you are free
Reply
Can’t
Best wishes / love
And also
Can i tell you
As i mentioned before`
        },
    ];

    // Left sidebar: ebooks
    const leftBoxes = [
        { id: 1, image: 'https://engdemyeducation.com/image/writingcover.jpg', text: 'Buy Writing Ebook', link: '#' },
        { id: 2, image: 'https://engdemyeducation.com/image/speakingcover.jpg', text: 'Buy Speaking Ebook', link: '#' },
        { id: 3, image: 'https://engdemyeducation.com/image/readingcover.jpg', text: 'Buy Reading Ebook', link: '#' },
    ];

    // Right sidebar: tests / AI
    const rightBoxes = [
        { id: 1, image: 'https://engdemyeducation.com/image/mocktests.jpg', text: 'Buy Mock Tests', link: '#' },
        { id: 2, image: 'https://engdemyeducation.com/image/aichecker.jpg', text: 'AI Writing Checker', link: '#' },
    ];

    const currentIndex = parseInt(chapterId, 10) - 1;
    if (currentIndex < 0 || currentIndex >= chapters.length) return <p>Chapter not found!</p>;

    const chapter = chapters[currentIndex];

    // Styles
    const containerStyle = {
        display: 'flex',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '20px',
        padding: '20px'
    };

    const sidebarStyle = {
        width: '240px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        position: 'sticky',
        top: '20px'
    };

    const boxStyle = {
        border: '1px solid #ccc',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        textAlign: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
    };

    const boxImageStyle = {
        width: '100%',
        height: '140px',
        objectFit: 'cover'
    };

    const boxTextStyle = {
        padding: '10px',
        backgroundColor: '#800000',
        color: '#fff',
        fontWeight: 'bold'
    };

    const contentStyle = {
        flex: 1,
        backgroundColor: '#fff',
        padding: '25px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    };

    const buttonStyle = {
        padding: '10px 16px',
        margin: '10px 5px 20px 0',
        backgroundColor: '#800000',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
    };

    return (
        <div>
            <h1 style={{ textAlign: 'center', color: '#800000', marginBottom: '30px' }}>CELPIP Writing Chapters</h1>
            <div style={containerStyle}>
                {/* Left Ebooks */}
                <div style={sidebarStyle}>
                    {leftBoxes.map(box => (
                        <a key={box.id} href={box.link} target="_blank" rel="noreferrer">
                            <div style={boxStyle}>
                                <img src={box.image} alt={box.text} style={boxImageStyle} />
                                <div style={boxTextStyle}>{box.text}</div>
                            </div>
                        </a>
                    ))}
                </div>

                {/* Chapter Content */}
                <div style={contentStyle}>
                    <button style={buttonStyle} onClick={() => navigate('/writing')}>← Back to Course</button>
                    <h2 style={{ color: '#800000', marginBottom: '20px' }}>Chapter {chapter.id}: {chapter.title}</h2>
                    <pre style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{chapter.content}</pre>

                    <div style={{ marginTop: '30px' }}>
                        {currentIndex > 0 && (
                            <button style={buttonStyle} onClick={() => navigate(`/writing/chapter/${currentIndex}`)}>← Previous</button>
                        )}
                        {currentIndex < chapters.length - 1 && (
                            <button style={buttonStyle} onClick={() => navigate(`/writing/chapter/${currentIndex + 2}`)}>Next →</button>
                        )}
                    </div>
                </div>

                {/* Right Tests / AI */}
                <div style={sidebarStyle}>
                    {rightBoxes.map(box => (
                        <a key={box.id} href={box.link} target="_blank" rel="noreferrer">
                            <div style={boxStyle}>
                                <img src={box.image} alt={box.text} style={boxImageStyle} />
                                <div style={boxTextStyle}>{box.text}</div>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default WritingChapterDetail;




































